/**
 * Staff (players asked: "a staff app, so we can hire drivers, a gateman, private chefs, security, at a price"): people
 * you employ, each paid a day's wage every morning while they work for you, each doing one real thing:
 *  - a driver takes you in your own car (you in the back, free; the car's yours, the fuel too);
 *  - a gateman stands at your gate; security, two guards in suits at your home;
 *  - a private chef cooks: a meal at home once a day, on the house;
 *  - a housekeeper cleans and does the laundry: you start every day fresh.
 * Can't pay them one morning: they leave (and say so). Wages are Lagos amounts, scaled to where you live.
 */
import { dayOf, message, spend, type LifeEvent, type LifeState } from './life.ts';
import { homePrice } from './character.ts';

const clamp = (v: number) => Math.max(0, Math.min(100, v));

export type StaffId = 'driver' | 'gateman' | 'security' | 'chef' | 'housekeeper';
export interface StaffDef { id: StaffId; label: string; emoji: string; /** a day's wage (Lagos naira) */ wage: number; blurb: string; sex: 'm' | 'f' }
export const STAFF: StaffDef[] = [
  { id: 'driver', label: 'Driver', emoji: '🚗', wage: 15_000, sex: 'm', blurb: 'Takes you anywhere in your own car: you in the back, no fare. (You need a car.)' },
  { id: 'gateman', label: 'Gateman', emoji: '🚪', wage: 6_000, sex: 'm', blurb: 'At your gate day and night: opens up, watches the compound.' },
  { id: 'security', label: 'Security (two guards)', emoji: '🕶️', wage: 25_000, sex: 'm', blurb: 'Two guards in suits at your home, one at the gate, one at the door.' },
  { id: 'chef', label: 'Private chef', emoji: '👨‍🍳', wage: 12_000, sex: 'f', blurb: 'Cooks at home: a full meal ready for you once a day, on the house.' },
  { id: 'housekeeper', label: 'Housekeeper', emoji: '🧺', wage: 8_000, sex: 'f', blurb: 'Cleans, washes, irons: you start every day fresh.' },
];
export const staffDef = (id: StaffId) => STAFF.find((d) => d.id === id)!;

export interface Hired { id: StaffId; name: string; since: number; /** (the chef) the day the last meal was had */ fed?: number }
type WithStaff = LifeState & { staff?: Hired[] };
export const staffOf = (s: LifeState): Hired[] => ((s as WithStaff).staff ??= []);
export const hasStaff = (s: LifeState, id: StaffId) => staffOf(s).find((h) => h.id === id) ?? null;
/** a day's wage for them, at your prices */
export const staffWage = (s: LifeState, id: StaffId) => homePrice(s, staffDef(id).wage, 500);

/** take them on (the first day's wage now) */
export function hireStaff(s: LifeState, id: StaffId, name: string): { ok: boolean; line: string } {
  if (hasStaff(s, id)) return { ok: false, line: `You already have a ${staffDef(id).label.toLowerCase()}.` };
  const wage = staffWage(s, id);
  if (!spend(s, wage, `Wages · ${staffDef(id).label} (${name})`)) return { ok: false, line: 'You can’t pay the first day’s wage yet.' };
  staffOf(s).push({ id, name, since: s.minutes });
  return { ok: true, line: `${name} starts today: ${staffDef(id).blurb.split(':')[0].toLowerCase()}.` };
}
export function letGo(s: LifeState, id: StaffId): string {
  const h = hasStaff(s, id);
  if (!h) return '';
  (s as WithStaff).staff = staffOf(s).filter((x) => x.id !== id);
  return `${h.name.split(' ')[0]} thanks you, collects their things and goes.`;
}

/** every morning: the wages (anyone you can't pay leaves), and the housekeeper's clean clothes */
export function staffMorning(s: LifeState, events: LifeEvent[]) {
  for (const h of [...staffOf(s)]) {
    const d = staffDef(h.id), wage = staffWage(s, h.id);
    if (spend(s, wage, `Wages · ${d.label} (${h.name.split(' ')[0]})`)) { if (h.id === 'housekeeper') s.needs.hygiene = clamp(Math.max(s.needs.hygiene, 85)); continue; }
    (s as WithStaff).staff = staffOf(s).filter((x) => x !== h);
    message(s, h.name, `Good morning. There was no money for my pay today, so I've gone. If things change, you know where to find me.`, events);
  }
}

/** the chef's meal: once a day, at home (null: none today, or no chef) */
export function chefMeal(s: LifeState): string | null {
  const h = hasStaff(s, 'chef'), day = dayOf(s.minutes);
  if (!h || h.fed === day) return null;
  h.fed = day;
  s.needs.hunger = clamp(Math.max(s.needs.hunger, 95)); s.needs.fun = clamp(s.needs.fun + 5);
  return `${h.name.split(' ')[0]} has made ${['jollof rice and chicken, plantain on the side', 'pounded yam and egusi', 'grilled fish with peppered sauce', 'a pot of ofada stew and rice', 'pasta with a proper ragù', 'a steak, done the way you like it'][day % 6]}. You eat well.`;
}
